<template>
  <v-container fluid class="users-page pa-8 pt-12">
    <div class="users-header">
      <div class="users-header__titles">
        <h1 class="users-title">Configurações - Usuários</h1>
        <p class="users-subtitle">
          Controle os usuários da sua empresa e configure seus níveis de permissão.
        </p>
      </div>

      <v-btn class="users-new-btn" depressed @click="handleNovoUsuario">
        <v-icon left size="18">mdi-plus</v-icon>
        Novo usuário
      </v-btn>
    </div>

    <v-row class="mt-6" dense>
      <v-col cols="12" md="3">
        <v-card class="users-stat" outlined>
          <div class="users-stat__content">
            <div>
              <div class="users-stat__label">Total de usuários</div>
              <div class="users-stat__value">{{ totalUsuarios }}</div>
            </div>
            <div class="users-stat__icon users-stat__icon--primary">
              <v-icon color="primary" size="20">mdi-account-multiple-outline</v-icon>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="users-stat" outlined>
          <div class="users-stat__content">
            <div>
              <div class="users-stat__label">Usuários ativos</div>
              <div class="users-stat__value users-stat__value--accent">{{ usuariosAtivos }}</div>
            </div>
            <div class="users-stat__icon users-stat__icon--accent">
              <v-icon :color="accentColor" size="20">mdi-account-check-outline</v-icon>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="users-stat" outlined>
          <div class="users-stat__content">
            <div>
              <div class="users-stat__label">Usuários inativos</div>
              <div class="users-stat__value error--text">{{ usuariosInativos }}</div>
            </div>
            <div class="users-stat__icon users-stat__icon--muted">
              <v-icon color="error" size="20">mdi-account-cancel-outline</v-icon>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="users-stat" outlined>
          <div class="users-stat__content">
            <div>
              <div class="users-stat__label">Administradores</div>
              <div class="users-stat__value users-stat__value--accent">{{ administradores }}</div>
            </div>
            <div class="users-stat__icon users-stat__icon--accent">
              <v-icon :color="accentColor" size="20">mdi-shield-account-outline</v-icon>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="users-table-card mt-6" outlined>
      <v-data-table
        class="users-table"
        :headers="headers"
        :items="tableItems"
        :loading="loading"
        loading-text="Carregando usuários"
        no-data-text="Nenhum usuário cadastrado"
        no-results-text="Nenhum resultado encontrado. Por favor, tente outra pesquisa."
        :items-per-page="8"
        :footer-props="{
          itemsPerPageText: 'Itens por Página',
          itemsPerPageOptions: [10, 15, 20],
          pageText: '{0}-{1} de {2}'
        }"
      >
        <template v-slot:[`item.status`]="{ item }">
          <span class="users-status" :class="item.isAtivo ? 'is-active' : 'error--text'">
            {{ item.statusLabel }}
          </span>
        </template>

        <template v-slot:[`item.perfil`]="{ item }">
          <div class="users-profile">
            <template v-if="item.isAdmin">
              <v-icon :color="accentColor" size="18" class="mr-1">mdi-shield-outline</v-icon>
              <span :style="{ color: accentColor }">Administrador</span>
            </template>
            <template v-else>
              <span>Usuário</span>
            </template>
          </div>
        </template>

        <template v-slot:[`item.acoes`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="users-actions"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon :color="accentColor">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list dense class="users-actions__menu">
              <v-list-item :disabled="statusUpdatingId === item.id" @click="editarUsuario(item)">
                <v-list-item-icon>
                  <v-icon :color="accentColor" size="18">mdi-pencil-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Editar</v-list-item-title>
              </v-list-item>

              <v-divider />

              <v-list-item  v-if="!item.isAdmin" :disabled="statusUpdatingId === item.id" @click="toggleStatus(item)">
                <v-list-item-icon>
                  <v-progress-circular
                    v-if="statusUpdatingId === item.id"
                    indeterminate
                    size="18"
                    width="2"
                    :color="accentColor"
                  />
                  <v-icon v-else :color="item.isAtivo ? 'error' : accentColor" size="18">
                    {{ item.isAtivo ? 'mdi-account-off-outline' : 'mdi-account-check-outline' }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  {{ item.isAtivo ? 'Inativar' : 'Ativar' }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialogOpen" max-width="440" persistent>
      <v-card class="users-dialog" outlined>
        <v-card-title class="users-dialog__title">
          <span>{{ dialogTitle }}</span>
          <v-spacer />
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-6 pb-0">
          <v-form ref="usuarioForm" v-model="formValid" lazy-validation>
            <v-text-field
              v-model.trim="form.nome"
              label="Nome"
              outlined
              dense
              :rules="rules.nome"
            />

            <v-text-field
              v-model.trim="form.email"
              label="E-mail"
              outlined
              dense
              :rules="rules.email"
            />

            <v-text-field
              v-model="form.perfil"
              label="Perfil"
              outlined
              dense
              disabled
            />

            <v-text-field
              v-model="form.senha"
              label="Senha"
              outlined
              dense
              :type="showPassword ? 'text' : 'password'"
              :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              @click:append="showPassword = !showPassword"
              :rules="passwordRules"
              autocomplete="new-password"
            />

            <v-text-field
              v-model="form.confirmarSenha"
              label="Confirmar senha"
              outlined
              dense
              :type="showPassword ? 'text' : 'password'"
              :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              @click:append="showPassword = !showPassword"
              :rules="confirmPasswordRules"
              autocomplete="new-password"
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer />
          <v-btn text :disabled="saving" @click="closeDialog">Cancelar</v-btn>
          <v-btn class="users-dialog__save" depressed :loading="saving" :disabled="saving" @click="saveUsuario">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import UsuarioServices from './usuarioServices';
    export default {
      name: 'listagemUsuarios',
    
      data() {
        return {
          accentColor: '#10b981',
          loading: false,
          statusUpdatingId: null,
          usuarios: [],
          dialogOpen: false,
          saving: false,
          formValid: false,
          showPassword: false,
          form: {
            id: null,
            nome: '',
            email: '',
            perfil: 'Usuário',
            senha: '',
            confirmarSenha: '',
            raw: null,
          },
          rules: {
            nome: [(v) => Boolean(v) || 'Informe o nome'],
            email: [
              (v) => Boolean(v) || 'Informe o e-mail',
              (v) => /.+@.+\..+/.test(String(v || '')) || 'E-mail inválido',
            ],
          },
          headers: [
            { text: 'NOME', value: 'nome', sortable: false },
            { text: 'STATUS', value: 'status', sortable: false },
            { text: 'E-MAIL', value: 'email', sortable: false },
            { text: 'PERFIL', value: 'perfil', sortable: false },
            { text: 'AÇÕES', value: 'acoes', sortable: false, align: 'center' },
          ],
        };
      },
    
      computed: {
        tableItems() {
          return Array.isArray(this.usuarios)
            ? this.usuarios.map((u) => this.normalizeUsuario(u))
            : [];
        },
      
        totalUsuarios() {
          return this.tableItems.length;
        },
      
        usuariosAtivos() {
          return this.tableItems.filter((u) => u.isAtivo).length;
        },
      
        usuariosInativos() {
          return this.tableItems.filter((u) => !u.isAtivo).length;
        },
      
        administradores() {
          return this.tableItems.filter((u) => u.isAdmin).length;
        },
      
        isEditMode() {
          return Boolean(this.form?.id);
        },
      
        dialogTitle() {
          return this.isEditMode ? 'Editar usuário' : 'Novo usuário';
        },
      
        // ===== SENHA =====
        passwordRules() {
          if (this.isEditMode) {
            return [
              (v) => !v || v.length >= 6 || 'Senha deve ter no mínimo 6 caracteres',
            ];
          }
        
          return [
            (v) => Boolean(v) || 'Informe a senha',
            (v) => v.length >= 6 || 'Senha deve ter no mínimo 6 caracteres',
          ];
        },
      
        confirmPasswordRules() {
          const senha = String(this.form?.senha || '');
        
          if (this.isEditMode) {
            if (!senha) return [];
          
            return [
              (v) => Boolean(v) || 'Confirme a senha',
              (v) => v === senha || 'As senhas não conferem',
            ];
          }
        
          return [
            (v) => Boolean(v) || 'Confirme a senha',
            (v) => v === senha || 'As senhas não conferem',
          ];
        },
      },
    
      mounted() {
        this.listarUsuarios();
      },
    
      methods: {
        handleNovoUsuario() {
          this.form = {
            id: null,
            nome: '',
            email: '',
            perfil: 'Usuário',
            senha: '',
            confirmarSenha: '',
            raw: null,
          };
        
          this.formValid = false;
          this.showPassword = false;
          this.dialogOpen = true;
        
          this.$nextTick(() => {
            this.$refs?.usuarioForm?.resetValidation?.();
          });
        },
      
        editarUsuario(item) {
          const raw = item?.raw || null;
        
          this.form = {
            id: item?.id || raw?.id || raw?.usuario_id || raw?._id || null,
            nome: raw?.nome || raw?.name || item?.nome || '',
            email: raw?.email || item?.email || '',
            perfil: 'Usuário',
            senha: '',
            confirmarSenha: '',
            raw,
          };
        
          this.formValid = true;
          this.showPassword = false;
          this.dialogOpen = true;
        
          this.$nextTick(() => {
            this.$refs?.usuarioForm?.resetValidation?.();
          });
        },
      
        closeDialog() {
          if (this.saving) return;
          this.dialogOpen = false;
        },
      
        async saveUsuario() {
          const isValid = this.$refs?.usuarioForm?.validate?.();
          if (!isValid) return;
        
          this.saving = true;
        
          try {
            let payload = {
              nome: this.form.nome,
              email: this.form.email,
              tipo: 'USUARIO',
            };

            if (this.isEditMode) {
              if (this.form.senha && this.form.senha.trim().length > 0) {
                payload.senha = this.form.senha;
              }
              await UsuarioServices.atualizar(this.form.id, payload);
              this.$toasted?.show('Usuário atualizado com sucesso');
            } else {
              payload.senha = this.form.senha;
              await UsuarioServices.criar(payload);
              this.$toasted?.show('Usuário criado com sucesso');
            }

            this.dialogOpen = false;
            await this.listarUsuarios();
          } catch (error) {
            // Trata erro de e-mail já cadastrado
            if (error?.response?.status === 409) {
              this.$toasted?.error('E-mail já cadastrado No sistema. Escolha outro e-mail.');
            } else if (error?.response?.data?.error) {
              this.$toasted?.show(error.response.data.error);
            } else {
              this.$toasted?.show('Erro ao salvar usuário');
            }
            console.error('Erro ao salvar usuário:', error);
          } finally {
            this.saving = false;
          }
        },
      
        async toggleStatus(item) {
          if (!item?.id || this.statusUpdatingId) return;
        
          try {
            this.statusUpdatingId = item.id;
            await UsuarioServices.mudarStatus(item.id);
            await this.listarUsuarios();
          } catch (error) {
            console.error('Erro ao mudar status:', error);
          } finally {
            this.statusUpdatingId = null;
          }
        },
      
        normalizeStatus(valor) {
          if (typeof valor === 'boolean') return valor ? 'Ativo' : 'Inativo';
          if (typeof valor === 'number') return valor === 1 ? 'Ativo' : 'Inativo';
        
          const texto = String(valor || '').toLowerCase();
          return ['ativo', '1', 'true'].includes(texto) ? 'Ativo' : 'Inativo';
        },
      
        isAdminUsuario(usuario) {
          const campos = [
            usuario?.perfil,
            usuario?.tipo,
            usuario?.role,
            usuario?.papel,
          ]
            .filter(Boolean)
            .map((v) => String(v).toLowerCase());
        
          return campos.some((v) => v.includes('admin'));
        },
      
        normalizeUsuario(usuario) {
          const statusLabel = this.normalizeStatus(usuario?.status);
          const isAtivo = statusLabel === 'Ativo';
        
          return {
            id: usuario?.id || usuario?.usuario_id || usuario?._id,
            nome:
              usuario?.nome ||
              usuario?.name ||
              usuario?.usuario ||
              usuario?.email ||
              '-',
            email: usuario?.email || '-',
            statusLabel,
            isAtivo,
            isAdmin: this.isAdminUsuario(usuario),
            raw: usuario,
          };
        },
      
        async listarUsuarios() {
          try {
            this.loading = true;
            const response = await UsuarioServices.listar();
            this.usuarios = Array.isArray(response.data)
              ? response.data
              : [];
          } catch (error) {
            console.error('Erro ao listar usuários:', error);
          } finally {
            this.loading = false;
          }
        },
      },
    };
    </script>

<style scoped>
@import "../../assets/inter-font.css";

.users-page {
  font-family: "Inter", sans-serif;
}

.users-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.users-title {
  color: #10b981;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}

.users-subtitle {
  color: #7a8ca6;
  font-size: 13px;
  margin: 6px 0 0;
}

.users-new-btn {
  background-color: #10b981 !important;
  color: #ffffff !important;
  border-radius: 12px;
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0.01em;
  padding: 10px 16px;
}

.users-stat {
  border-radius: 16px;
  border-color: #e6ebf1 !important;
}

.users-stat__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 18px;
  gap: 12px;
}

.users-stat__label {
  color: #7a8ca6;
  font-weight: 600;
  font-size: 12px;
}

.users-stat__value {
  color: #1f2a44;
  font-weight: 800;
  font-size: 28px;
  margin-top: 6px;
}

.users-stat__value--accent {
  color: #10b981;
}

.users-stat__icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.users-stat__icon--primary {
  background: rgba(122, 140, 166, 0.12);
}

.users-stat__icon--accent {
  background: rgba(16, 185, 129, 0.12);
}


.users-stat__icon--muted {
  background: rgba(122, 140, 166, 0.12);
}

.users-table-card {
  border-radius: 16px;
  border-color: #e6ebf1 !important;
}

.users-table {
  border-radius: 16px;
}

.users-table ::v-deep th {
  color: #7a8ca6 !important;
  font-weight: 800 !important;
  font-size: 11px !important;
  letter-spacing: 0.08em;
}

.users-table ::v-deep td {
  color: #1f2a44;
  font-size: 13px;
}

.users-table ::v-deep .v-data-table__wrapper {
  border-radius: 16px;
}

.users-status {
  font-weight: 800;
}

.users-status.is-active {
  color: #10b981;
}


.users-profile {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 800;
}

.users-actions {
  opacity: 0.9;
}

.users-actions__menu {
  min-width: 180px;
}

.users-dialog {
  border-radius: 16px;
}

.users-dialog__title {
  font-weight: 800;
  color: #10b981;
}

.users-dialog__save {
  background-color: #10b981 !important;
  color: #ffffff !important;
  border-radius: 12px;
  text-transform: none;
  font-weight: 800;
}

.no-wrap {
  white-space: nowrap;
}
</style>
